import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Search, ClipboardList, ChevronRight, X as XIcon, Zap } from 'lucide-react';
import { apiService, Appointment, Provider, Procedure, resolveLocalized } from '../../services/apiService';
import PageHeader from './ui/PageHeader';
import StatusPill, { StatusTone } from './ui/StatusPill';
import EmptyState from './ui/EmptyState';
import { formatDate } from '../../i18n/locale';

const STATUS_META: Record<string, { labelKey: string; tone: StatusTone }> = {
  pending: { labelKey: 'appointments.page.statusPendingWebsite', tone: 'warning' },
  scheduled: { labelKey: 'appointments.page.statusScheduled', tone: 'info' },
  confirmed: { labelKey: 'appointments.page.statusConfirmed', tone: 'success' },
  completed: { labelKey: 'appointments.page.statusCompleted', tone: 'info' },
  cancelled: { labelKey: 'appointments.page.statusCancelled', tone: 'danger' },
  no_show: { labelKey: 'appointments.page.statusNoShow', tone: 'neutral' },
};

const inputClass =
  'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';

const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const AppointmentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [procedureFilter, setProcedureFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [a, p, r] = await Promise.all([
          apiService.getAppointments(),
          apiService.getProviders(false),
          apiService.getProcedures(false),
        ]);
        setAppointments(a.data.data || []);
        setProviders(p.data.data || []);
        setProcedures(r.data.data || []);
      } catch (e: any) {
        setError(e?.message || t('appointments.page.loadError'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return appointments
      .filter(a => {
        if (search && !`${a.patient_name} ${a.patient_email}`.toLowerCase().includes(search.toLowerCase())) return false;
        if (from && a.appointment_date < from) return false;
        if (to && a.appointment_date > to) return false;
        if (providerFilter && String(a.provider_id) !== providerFilter) return false;
        if (procedureFilter && String(a.procedure_id) !== procedureFilter) return false;
        if (statusFilter && a.status !== statusFilter) return false;
        return true;
      })
      .sort((x, y) =>
        `${x.appointment_date} ${x.start_time}`.localeCompare(`${y.appointment_date} ${y.start_time}`)
      );
  }, [appointments, search, from, to, providerFilter, procedureFilter, statusFilter]);

  const resetFilters = () => {
    setSearch(''); setFrom(''); setTo(''); setProviderFilter(''); setProcedureFilter(''); setStatusFilter('');
  };

  const hasActiveFilters = !!(search || from || to || providerFilter || procedureFilter || statusFilter);

  const fmtDate = (iso: string | null | undefined): string => {
    if (!iso) return '';
    return formatDate(`${iso.slice(0, 10)}T00:00:00`, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div>
      <PageHeader
        eyebrow={t('appointments.page.eyebrow')}
        title={t('appointments.page.title')}
        description={t('appointments.page.description')}
        actions={
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-brand-text-muted">
              <span className="font-semibold text-brand-text tabular-nums">{filtered.length}</span> {t('appointments.page.of')}{' '}
              <span className="tabular-nums">{appointments.length}</span>
            </span>
            <Link
              to="/admin/quick-appointment"
              className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-brand-primary-hover cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/50"
            >
              <Zap className="h-4 w-4" strokeWidth={2.25} aria-hidden />
              {t('quick.nav')}
            </Link>
          </div>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-3">
            <label className={labelClass}>{t('appointments.page.searchLabel')}</label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-muted pointer-events-none"
                strokeWidth={1.75}
                aria-hidden
              />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('appointments.page.searchPlaceholder')}
                className={`${inputClass} pl-9`}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>{t('appointments.page.from')}</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('appointments.page.to')}</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{t('common.status')}</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={inputClass}>
              <option value="">{t('common.all')}</option>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>{t(v.labelKey)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t('nav.providers')}</label>
            <select value={providerFilter} onChange={e => setProviderFilter(e.target.value)} className={inputClass}>
              <option value="">{t('common.all')}</option>
              {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t('nav.procedures')}</label>
            <select value={procedureFilter} onChange={e => setProcedureFilter(e.target.value)} className={inputClass}>
              <option value="">{t('common.all')}</option>
              {procedures.map(p => <option key={p.id} value={p.id}>{resolveLocalized(p.name)}</option>)}
            </select>
          </div>
        </div>
        {hasActiveFilters && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 rounded-sm px-1"
            >
              <XIcon className="h-3 w-3" strokeWidth={2} />
              {t('appointments.page.resetFilters')}
            </button>
          </div>
        )}
      </div>

      {/* List */}
      <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-brand-text-muted">{t('appointments.page.loading')}</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title={hasActiveFilters ? t('appointments.page.emptyHitsTitle') : t('appointments.page.emptyTitle')}
            description={
              hasActiveFilters
                ? t('appointments.page.emptyHitsDescription')
                : t('appointments.page.emptyDescription')
            }
          />
        ) : (
          <>
            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-brand-border">
                <thead className="bg-brand-surface-2/60">
                  <tr>
                    <Th>{t('appointments.page.colDateTime')}</Th>
                    <Th>{t('appointments.patient')}</Th>
                    <Th>{t('appointments.provider')}</Th>
                    <Th>{t('appointments.procedure')}</Th>
                    <Th>{t('appointments.page.room')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th align="right"><span className="sr-only">{t('common.actions')}</span></Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-border">
                  {filtered.map(a => {
                    const meta = STATUS_META[a.status];
                    const metaLabel = meta ? t(meta.labelKey) : a.status;
                    const tone = meta ? meta.tone : ('neutral' as StatusTone);
                    return (
                      <tr key={a.id} className="hover:bg-brand-surface-2/60 transition-colors">
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <div className="font-medium text-brand-text tabular-nums">{fmtDate(a.appointment_date)}</div>
                          <div className="text-xs text-brand-text-muted tabular-nums">{a.start_time}–{a.end_time}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-brand-text">{a.patient_name}</div>
                          <div className="text-xs text-brand-text-muted">{a.patient_email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-brand-text">{a.provider_name}</td>
                        <td className="px-4 py-3 text-sm text-brand-text">{a.procedure_name}</td>
                        <td className="px-4 py-3 text-sm text-brand-text">{a.operatory_name}</td>
                        <td className="px-4 py-3 text-sm">
                          <StatusPill tone={tone} dot>{metaLabel}</StatusPill>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <Link
                            to={`/admin/calendar?date=${a.appointment_date}&id=${a.id}`}
                            className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-primary-hover hover:underline whitespace-nowrap font-medium cursor-pointer"
                          >
                            {t('appointments.page.inCalendar')}
                            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile: card list */}
            <ul className="md:hidden divide-y divide-brand-border">
              {filtered.map(a => {
                const meta = STATUS_META[a.status];
                const metaLabel = meta ? t(meta.labelKey) : a.status;
                const tone = meta ? meta.tone : ('neutral' as StatusTone);
                return (
                  <li key={a.id}>
                    <Link
                      to={`/admin/calendar?date=${a.appointment_date}&id=${a.id}`}
                      className="block p-4 hover:bg-brand-surface-2/60 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-brand-text truncate">{a.patient_name}</div>
                          <div className="text-xs text-brand-text-muted tabular-nums">
                            {fmtDate(a.appointment_date)} · {a.start_time}–{a.end_time}
                          </div>
                          <div className="text-xs text-brand-text-muted mt-1">
                            {a.procedure_name} · {a.provider_name}
                          </div>
                        </div>
                        <StatusPill tone={tone}>{metaLabel}</StatusPill>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th
    className={`px-4 py-3 text-${align} text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted`}
  >
    {children}
  </th>
);

export default AppointmentsPage;
